import { useEffect, useState } from 'react'

type Section = {
  label: string
  key: string
}

type UseScrollSectionsProps = {
  defaultActive?: number
  sections: Section[]
  scrollOffset?: number
  highlightOffset?: number
}

export const useScrollSections = ({
  defaultActive = 0,
  sections,
  scrollOffset = 180, // Default offset for scroll to behavior
  highlightOffset = 300 // Default offset for when to highlight the tab
}: UseScrollSectionsProps) => {
  const [active, setActive] = useState(defaultActive)

  useEffect(() => {
    const handleScroll = () => {
      const container = document.getElementById('layout-data')
      if (!container) return

      const sectionElements = sections
        .map((section) => ({
          element: document.getElementById(section.key),
          key: section.key
        }))
        .filter((item) => item.element !== null)

      if (sectionElements.length === 0) return

      const containerRect = container.getBoundingClientRect()
      const containerTop = containerRect.top

      for (let i = 0; i < sectionElements.length; i++) {
        const { element } = sectionElements[i]
        if (!element) continue

        const rect = element.getBoundingClientRect()
        const relativeTop = rect.top - containerTop

        if (relativeTop >= highlightOffset) {
          setActive(Math.max(0, i - 1))
          break
        }
        if (i === sectionElements.length - 1) {
          setActive(i)
        }
      }
    }

    const container = document.getElementById('layout-data')
    if (container) {
      container.addEventListener('scroll', handleScroll, { passive: true })
      handleScroll()

      return () => container.removeEventListener('scroll', handleScroll)
    }
  }, [sections, highlightOffset])

  const handleChange = (index: number) => {
    setActive(index)
    const section = sections[index].key
    const element = document.getElementById(section)
    const container = document.getElementById('layout-data')

    if (element && container) {
      const elementPosition = element.getBoundingClientRect().top
      const containerTop = container.getBoundingClientRect().top
      const offsetPosition = elementPosition - containerTop - scrollOffset + container.scrollTop

      container.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      })
    }
  }

  return { active, handleChange }
}
